import { useState, useRef, useEffect } from 'react'
import { X, Search, MapPin, Loader2 } from 'lucide-react'
import { motion, AnimatePresence } from 'framer-motion'
import Button from '@/components/ui/Button'
import { useSearchAddress, type LocationSearchResult } from '@/features/location/api'
import { cn } from '@/lib/utils'

interface AddressSearchModalProps {
  /** 모달 열림 여부 */
  isOpen: boolean
  /** 닫기 콜백 */
  onClose: () => void
  /** 주소 선택 콜백 */
  onSelect: (result: LocationSearchResult) => void
}

/**
 * 카카오 주소 검색 모달.
 * 도로명/지번/건물명을 입력하여 주소를 검색하고 좌표를 얻는다.
 */
export default function AddressSearchModal({
  isOpen,
  onClose,
  onSelect,
}: AddressSearchModalProps) {
  const [query, setQuery] = useState('')
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const { mutate, data: results, isPending, reset } = useSearchAddress()

  // 모달 열릴 때 입력 필드에 포커스
  useEffect(() => {
    if (isOpen) {
      // 약간의 지연으로 애니메이션 후 포커스
      const timer = setTimeout(() => {
        inputRef.current?.focus()
      }, 300)
      return () => clearTimeout(timer)
    }
    // 모달 닫힐 때 상태 초기화
    setQuery('')
    setSelectedIndex(null)
    reset()
  }, [isOpen, reset])

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim().length < 2) return
    setSelectedIndex(null)
    mutate(query.trim())
  }

  const handleSelect = (index: number) => {
    setSelectedIndex(index)
  }

  const handleConfirm = () => {
    if (selectedIndex !== null && results && results[selectedIndex]) {
      onSelect(results[selectedIndex])
      onClose()
    }
  }

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* 오버레이 */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-black/40"
            onClick={onClose}
            aria-hidden="true"
          />

          {/* 모달 */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed inset-x-0 bottom-0 z-50 flex max-h-[85vh] flex-col rounded-t-2xl bg-white pb-[env(safe-area-inset-bottom)]"
            role="dialog"
            aria-label="주소 검색"
            aria-modal="true"
          >
            {/* 핸들 바 */}
            <div className="flex items-center justify-center pt-3 pb-2">
              <div className="h-1 w-10 rounded-full bg-cb-gray-300" />
            </div>

            {/* 헤더 */}
            <div className="flex items-center justify-between px-5 pb-3">
              <h2 className="text-h3 font-bold text-cb-gray-900">주소 검색</h2>
              <button
                onClick={onClose}
                className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-cb-gray-100"
                aria-label="주소 검색 닫기"
              >
                <X size={22} className="text-cb-gray-600" />
              </button>
            </div>

            {/* 검색 입력 */}
            <form onSubmit={handleSearch} className="px-5 pb-3">
              <div className="relative">
                <input
                  ref={inputRef}
                  type="search"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="도로명, 지번, 건물명 입력"
                  className={cn(
                    'h-[var(--size-input)] w-full rounded-xl border border-cb-gray-300 pl-4 pr-12',
                    'text-body text-cb-gray-900 placeholder:text-cb-gray-400',
                    'focus:outline-none focus:ring-2 focus:ring-cb-primary focus:border-cb-primary',
                    'transition-colors duration-150'
                  )}
                  aria-label="주소 검색어 입력"
                />
                <button
                  type="submit"
                  disabled={isPending || query.trim().length < 2}
                  className="absolute right-1 top-1/2 -translate-y-1/2 flex h-10 w-10 items-center justify-center rounded-xl bg-cb-primary text-white hover:bg-cb-primary-hover transition-colors disabled:opacity-50"
                  aria-label="검색"
                >
                  {isPending ? (
                    <Loader2 size={20} className="animate-spin" />
                  ) : (
                    <Search size={20} />
                  )}
                </button>
              </div>
            </form>

            {/* 검색 결과 */}
            <div className="flex-1 overflow-y-auto px-5">
              {/* 안내 메시지 */}
              {!results && !isPending && (
                <div className="py-8 text-center">
                  <MapPin size={40} className="mx-auto mb-3 text-cb-gray-300" />
                  <p className="text-body-sm text-cb-gray-400">
                    주소를 검색하여 위치를 변경해보세요
                  </p>
                  <p className="mt-1 text-xs text-cb-gray-300">
                    예: 수원시 영통구, 강남대로 108
                  </p>
                </div>
              )}

              {/* 결과 없음 */}
              {results && results.length === 0 && (
                <div className="py-8 text-center">
                  <p className="text-body-sm text-cb-gray-500">
                    검색 결과가 없어요
                  </p>
                  <p className="mt-1 text-xs text-cb-gray-400">
                    다른 검색어로 다시 시도해보세요
                  </p>
                </div>
              )}

              {/* 결과 목록 */}
              {results && results.length > 0 && (
                <ul className="divide-y divide-cb-gray-100" role="listbox" aria-label="검색 결과">
                  {results.map((result, index) => (
                    <li key={`${result.latitude}-${result.longitude}-${index}`}>
                      <button
                        onClick={() => handleSelect(index)}
                        className={cn(
                          'flex w-full items-start gap-3 px-2 py-3.5 text-left transition-colors rounded-lg',
                          selectedIndex === index
                            ? 'bg-cb-primary-light'
                            : 'hover:bg-cb-gray-50'
                        )}
                        role="option"
                        aria-selected={selectedIndex === index}
                      >
                        <MapPin
                          size={18}
                          className={cn(
                            'mt-0.5 flex-shrink-0',
                            selectedIndex === index ? 'text-cb-primary' : 'text-cb-gray-400'
                          )}
                        />
                        <span
                          className={cn(
                            'text-body-sm',
                            selectedIndex === index
                              ? 'font-medium text-cb-primary'
                              : 'text-cb-gray-700'
                          )}
                        >
                          {result.addressName}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* 하단 확인 버튼 */}
            {results && results.length > 0 && (
              <div className="border-t border-cb-gray-200 px-5 py-4">
                <Button
                  variant="primary"
                  size="lg"
                  fullWidth
                  onClick={handleConfirm}
                  disabled={selectedIndex === null}
                >
                  이 위치로 검색
                </Button>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}
